import {TFunction} from "i18next";
import {SupportedLanguages} from "@/enums/internationalization";
import type {BrandConfig} from "@/types/branding";
import {GptDetails} from "@/types/chat";
import { apiFetch } from "@/lib/api";
import {BASE_GAIL} from "./common/baseUrls";

type ChatLanguageOptions = {
   defaultLanguage?: string | null;
   queryLanguage?: string | null;
   browserLanguage?: string | null;
   fallbackLanguage?: string;
};

const firstNonEmptyLanguage = (...values: Array<string | null | undefined>): string | undefined =>
   values.find((value) => typeof value === "string" && value.trim().length > 0)?.trim();

export const ResolveChatLanguage = ({
   defaultLanguage,
   queryLanguage,
   browserLanguage,
   fallbackLanguage = "en-US",
}: ChatLanguageOptions): string =>
   firstNonEmptyLanguage(
      queryLanguage,
      defaultLanguage,
      browserLanguage,
      fallbackLanguage
   ) ||
   "en-US";

export const ResolveSupportedLanguageKey = (
   language: string | null | undefined,
   fallback: SupportedLanguages = SupportedLanguages.English
): SupportedLanguages => {
   const normalizedBaseLanguage = language?.trim().toLowerCase().split("-")[0];

   if (
      normalizedBaseLanguage &&
      Object.values(SupportedLanguages).includes(normalizedBaseLanguage as SupportedLanguages)
   ) {
      return normalizedBaseLanguage as SupportedLanguages;
   }

   return fallback;
};

export const GetAssistantChatChannel = async (): Promise<GptDetails | null> => {
   if (!globalThis.window) {
      return null;
   }

   const location = globalThis.window.location?.href;

   if (!location?.startsWith(BASE_GAIL)) {
      return null;
   }

   const splitLocation = location.split("?")[0].split("/");

   if (splitLocation.length < 2) {
      return null;
   }

   const orgId = splitLocation[splitLocation.length - 2];
   const agentId = splitLocation[splitLocation.length - 1];

   const details = await apiFetch<GptDetails>(`${BASE_GAIL}/api/public/gpt`, "GET", {
      headers: {
         "X-Org-ID": orgId,
         "X-Agent-ID": agentId,
      },
   });

   return details;
};

export {pickGreetingKey} from "@repo/ui/gailgpt/greeting";

/**
 * Returns the GPT product name for the given brand.
 * When config is provided (e.g. from useBrandConfig), uses config.gptName; otherwise falls back to default translation.
 */
export const GetGailGPTBrandName = (
   brand: string,
   t: TFunction,
   config?: BrandConfig | null
): string => {
   if (config?.gptName) return config.gptName;
   return t("askGail.default");
};
